import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { API_URL } from "../../config";
import LoadingSpinner from "../../components/LoadingSpinner";
import { Link, useNavigate, useParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import Swal from "sweetalert2";
import moment from "moment/moment";
import { PermissionWrapper, hasPermission } from "../../helpers/permission.js";

const TicketList = () => {
  const [ticketList, setTicketList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { catid } = useParams();
  const navigate = useNavigate();

  // Filters
  const [subscriberFilter, setSubscriberFilter] = useState("");
  const [mobileNumberFilter, setMobileNumberFilter] = useState("");
  const [queryFilter, setQueryFilter] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;
  const token = localStorage.getItem("authToken");

  const handleChange = (dates) => {
    if (!dates) {
      // Clear both startDate and endDate if DatePicker is cleared
      setStartDate(null);
      setEndDate(null);
      return;
    }

    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  // Fetch symbols from API
  const fetchTicket = async (catid) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}Ticket/index`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          limit: limit,
          page: page,
          catid: catid,
          subscriber: subscriberFilter,
          mobile: mobileNumberFilter,
          query: queryFilter,
          status: statusFilter,
          ...(startDate &&
            endDate && {
            screated_on: startDate.toLocaleDateString("en-CA"),
            ecreated_on: endDate.toLocaleDateString("en-CA"),
          }),
        },
      });
      const data = response.data;
      setTicketList(data.ticketList);
      setPageCount(data.links.totalPages);
      setTotal(data.totalRecords);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (catid) {
      fetchTicket(catid);
    }
  }, [catid, page, statusFilter]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this record!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonClass: "btn-danger",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.delete(`${API_URL}Ticket/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        setTicketList((prev) => prev.filter((dt) => dt.id !== id));

        Swal.fire(
          "Deleted!",
          response.data.message || "Your record has been deleted.",
          "success"
        );
      } catch (error) {
        Swal.fire(
          "Error",
          error.response?.data?.message ||
          "Something went wrong while deleting.",
          "error"
        );
      }
    }
  };

  useEffect(() => {
    const handleClearTicketFilters = () => {
      setSubscriberFilter("");
      setMobileNumberFilter("");
      setQueryFilter("");
      setStatusFilter("");
      setStartDate(null);
      setEndDate(null);
      setPage(1);

      // optionally refetch after clearing
      if (catid) {
        fetchTicket(catid);
      }
    };

    window.addEventListener(
      "clearTicketFiltersEvent",
      handleClearTicketFilters
    );

    return () => {
      window.removeEventListener(
        "clearTicketFiltersEvent",
        handleClearTicketFilters
      );
    };
  }, [catid]);


  return (
    <div className="page-header-fixed page-sidebar-fixed fade in mt-5">
      {/* <div id="content">{loading && <LoadingSpinner />}</div> */}
      <div className="panel  panel-default with-rounded-corner with-shadow">
        <div className="d-flex align-items-center justify-content-between">
          <div className="panel-heading border-0">
            <h4 className="panel-title">Manage Ticket</h4>
          </div>
          <div className="me-3 ">
            <span style={{ fontSize: "0.8rem" }} className="me-2">
              {start} to {end} of {total}
            </span>
            <button
              className="btn btn-default btn-sm"
              title="Previous"
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            &nbsp;
            <button
              className="btn btn-default btn-sm"
              title="Next"
              onClick={() => handlePageChange(page + 1)}
              disabled={page === pageCount}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>
        <div className="table-responsive user-table-container">
          <style>
            {`
                  .user-table-container {
                    position: relative;
                    max-height: 75vh;
                    overflow-y: auto;
                    overflow-x: auto;
                   }

                  /* First header row (titles) */
                  .user-table-container table thead tr:nth-child(1) th {
                    position: sticky;
                    top: 0;
                    z-index: 30;
                    background: #f8f9fa;
                  }

                 /* Second header row (search inputs) */
                 .user-table-container table thead tr:nth-child(2) th {
                   position: sticky;
                   top: 25px; /* adjust based on header height */
                   z-index: 25;
                   background: #fff;
                  }
               `}
          </style>
          <table className="table table-hover bg-white">
            <thead className="thead-light">
              <tr>
                <th className="text-center">#</th>
                <th>Category</th>
                <th>Subscriber</th>
                <th>Mobile No.</th>
                <th>Query</th>
                <th>Created On</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
              <tr>
                <th></th>
                <th></th>
                <th>
                  <input
                    type="text"
                    placeholder="Subscriber"
                    value={subscriberFilter}
                    onChange={(e) => setSubscriberFilter(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") fetchTicket(catid);
                    }}
                    style={{ padding: "5px" }}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    placeholder="Mobile Number"
                    value={mobileNumberFilter}
                    onChange={(e) => setMobileNumberFilter(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") fetchTicket(catid);
                    }}
                    style={{ padding: "5px" }}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    placeholder="Query"
                    value={queryFilter}
                    onChange={(e) => setQueryFilter(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") fetchTicket(catid);
                    }}
                    style={{ padding: "5px" }}
                  />
                </th>
                <th>
                  <div className="d-flex align-items-center gap-1">
                    <DatePicker
                      selected={startDate}
                      onChange={handleChange}
                      startDate={startDate}
                      endDate={endDate}
                      selectsRange
                      isClearable
                      monthsShown={3}
                      placeholderText="Select a date range"
                    />
                    <button
                      type="button"
                      className="btn btn-default btn-xs btn-group-xs"
                      onClick={() => fetchTicket(catid)}
                    >
                      <FontAwesomeIcon icon={faSearch} />
                    </button>
                  </div>
                </th>
                <th>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{ padding: "5px" }}
                  >
                    <option value="" selected="selected">
                      Show All
                    </option>
                    <option value="0">Requested</option>
                    <option value="1">Open</option>
                    <option value="2">Closed</option>
                  </select>
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 8 }).map((_, index) => (
                  <tr key={index} className="skeleton-row">
                    <td className="text-center">
                      <div className="skeleton-box" />
                    </td>
                    <td><div className="skeleton-box" /></td>
                    <td><div className="skeleton-box" /></td>
                    <td><div className="skeleton-box" /></td>
                    <td><div className="skeleton-box" /></td>
                    <td><div className="skeleton-box" /></td>
                    <td><div className="skeleton-box" /></td>
                    <td><div className="skeleton-box" /></td>
                  </tr>
                ))
              ) : ticketList.length > 0 ? (
                ticketList.map((post, index) => {
                  let status = (
                    <div className="btn btn-danger btn-xs">Closed</div>
                  );
                  if (post.ticketStatus == 0) {
                    status = (
                      <div className="btn btn-info btn-xs">Requested</div>
                    );
                  } else if (post.ticketStatus == 1) {
                    status = <div className="btn btn-primary btn-xs">Open</div>;
                  }

                  return (
                    <tr
                      key={post.id}
                      onClick={() => {
                        if (!hasPermission("ticket_listing", "edit")) {
                          return; // no navigation without edit permission
                        }
                        navigate(
                          `/Ticket/Main/tickets/${catid}/edit/${post.id}`,
                          {
                            state: {
                              ticketIds: ticketList.map((t) => t.id), // passing filtered ticket IDs
                            },
                          }
                        );
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="text-center">
                        {(page - 1) * limit + index + 1}
                      </td>
                      <td>{post.categoryName}</td>
                      <td
                        style={{ color: "#0d6efd", cursor: "pointer" }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.textDecoration = "underline")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.textDecoration = "none")
                        }
                      >
                        {hasPermission("subscriber", "edit") ? (
                          <a
                            href={`/Subscriber/Edit/${post.subscriberId}`}
                            onClick={(e) => {
                              e.preventDefault(); // prevent default anchor navigation
                              e.stopPropagation();
                              navigate(`/Subscriber/Edit/${post.subscriberId}`);
                            }}
                            style={{
                              color: "#0d6efd",
                              textDecoration: "inherit", // inherit underline from td hover
                            }}
                          >
                            {`${post.fullName ?? ""} (${post.subscriberIdText ?? ""
                              })`}
                          </a>
                        ) : (
                          <span style={{ color: "#0d6efd" }}>
                            {`${post.fullName ?? ""} (${post.subscriberIdText ?? ""
                              })`}
                          </span>
                        )}
                      </td>

                      <td
                        style={{ color: "#0d6efd", cursor: "pointer" }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.textDecoration = "underline")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.textDecoration = "none")
                        }
                      >
                        {hasPermission("ticket_listing", "view") ? (
                          <Link
                            to={`tel:${post.mobileNo}`}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              color: "#0d6efd",
                              textDecoration: "inherit",
                            }} // inherit so underline works on td hover
                          >
                            {post.mobileNo}
                          </Link>
                        ) : (
                          <span style={{ color: "#0d6efd" }}>
                            {post.mobileNo}
                          </span>
                        )}
                      </td>

                      <td>{post.ticketTitle}</td>
                      <td className="ctc">
                        {moment(post.createdOn).format("DD-MM-YYYY hh:mm A")}
                      </td>
                      <td>{status}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <PermissionWrapper
                            module="ticket_listing"
                            action="delete"
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(post.id);
                              }}
                              className="cancel1 btn btn-danger btn-xs"
                              title="Delete"
                            >
                              <i className="ti ti-trash"></i>
                            </button>
                          </PermissionWrapper>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="9" className="text-center py-4">
                    No ticket found matching your criteria
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TicketList;
